import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  requestWorkspaceRoleRefresh,
  subscribeAccessDenied,
} from '@/features/auth/accessDeniedSignal';
import { roleChangedMessage, routeStillAllowed } from '@/features/auth/roleChangePolicy';
import { hasUnsavedEdits } from '@/features/auth/unsavedEdits';
import { useAuth } from '@/state/AuthContext';

const REFRESH_MS = 120_000;

function roleOf(user: { platformRole?: string; role?: string } | null | undefined): string | undefined {
  return user?.platformRole || user?.role;
}

/**
 * ROLE-CHANGE-LIVE-FE-1. Refetch the signed-in user on focus, every two
 * minutes while the tab is visible, and after a non-CSRF 403. A route the
 * new role cannot open goes to Home unless the page has unsaved edits.
 */
export function RoleSessionWatch() {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const roleRef = useRef<string | undefined>(roleOf(user));
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    const apply = async () => {
      const previous = roleRef.current;
      const nextUser = await refreshMe();
      if (cancelled) return;
      const next = roleOf(nextUser);
      roleRef.current = next;
      if (!previous || !next || previous === next) return;
      if (routeStillAllowed(pathRef.current, next)) return;
      const message = roleChangedMessage(next);
      setNotice(message);
      if (hasUnsavedEdits()) return;
      navigate('/home', { replace: true, state: { roleNotice: message } });
    };

    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') void apply();
    };
    window.addEventListener('focus', refreshIfVisible);
    const timer = window.setInterval(refreshIfVisible, REFRESH_MS);
    const unsubscribe = subscribeAccessDenied(() => {
      requestWorkspaceRoleRefresh();
      void apply();
    });
    return () => {
      cancelled = true;
      window.removeEventListener('focus', refreshIfVisible);
      window.clearInterval(timer);
      unsubscribe();
    };
  }, [user?.id, refreshMe, navigate]);

  const fromHome = (location.state as { roleNotice?: string } | null)?.roleNotice;
  const text = notice || fromHome || null;
  if (!text) return null;
  return (
    <p role="status" data-testid="role-change-notice" className="bg-amber-50 px-4 py-2 text-sm text-amber-950">
      {text}
    </p>
  );
}
